// ═══════════════════════════════════════════════════════════════
// Settings → Dashboard — Home page widget picker
// Choose which widgets fill the 3-col grid on the home page.
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Title, Text } from '../../components/gab/GabComponents';
import { IconCheck, IconGripVertical, IconRefresh, IconClipboardList, IconCalendarEvent, IconShieldCheck } from '@tabler/icons-react';
import { WIDGETS, HOME_WIDGET_KEY, HOME_WIDGET_DEFAULT } from '../dashboard/COP';
import { NATIVE_WIDGETS } from '../dashboard/Dashboard';
import { notifications } from '../../lib/notifications';

function loadSaved(): string[] {
  try { const s = localStorage.getItem(HOME_WIDGET_KEY); return s ? JSON.parse(s) : HOME_WIDGET_DEFAULT; }
  catch { return HOME_WIDGET_DEFAULT; }
}

function persist(ids: string[]) {
  localStorage.setItem(HOME_WIDGET_KEY, JSON.stringify(ids));
}

// Icons for native widgets
const NATIVE_ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  readiness: IconShieldCheck,
  schedule:  IconCalendarEvent,
  pending:   IconClipboardList,
};

const NATIVE_DESCRIPTIONS: Record<string, string> = {
  readiness: 'PACE roster, volunteer credentials, training compliance, and plans currency',
  schedule:  "Today's battle rhythm events — briefings, reports, and shift changes",
  pending:   'Assessments in review, active workflows, and open PA projects',
};

const COP_DESCRIPTIONS: Record<string, string> = {
  life_safety:     'Intake triage severity + structural damage counts',
  op_period:       'Operational period clock, countdown, and next briefing',
  weather:         'Live conditions at incident location via Open-Meteo',
  map_overview:    'Situation map with shelters and active zones',
  evac_zones:      'Active evacuation, impact, and staging zones',
  ics_command:     'IC name, location, ICS section structure, EOC headcount',
  battle_rhythm:   "Today's scheduled events from battle rhythm",
  resource_status: 'Volunteer fill rate, deployed count, EOC check-ins',
  incident_stats:  'Quick-glance numbers across all modules',
  shelter_occupancy:'Shelter open/closed status and capacity bars',
  intake_triage:   'Active intake cases by triage color',
  business_status: 'Operational status of businesses in Main Street',
  pa_pipeline:     'PA projects by phase (formulation → closeout)',
  pa_financial:    'Estimated vs obligated vs drawn-down PA dollars',
  pa_rfr:          'Reimbursement request count and approval status',
  debris_progress: 'Load ticket count, verified %, and debris types',
  impact_summary:  'Damage assessments by category',
  volunteers:      'Volunteer headcount, deployed, and completed',
  rollcall:        'Open roll call events and current EOC check-ins',
  activity_feed:   'Recent handoff notes and shift activity',
};

export default function DashboardTab() {
  const [selected, setSelected] = useState<string[]>(loadSaved);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = prev.includes(id) ? prev.filter(w => w !== id) : [...prev, id];
      persist(next);
      return next;
    });
  };

  // Drag-to-reorder the selected pill list
  function onDragStart(id: string) { setDragId(id); }
  function onDragEnd() { setDragId(null); setDragOver(null); }
  function onDragOver(e: React.DragEvent, id: string) { e.preventDefault(); if (id !== dragId) setDragOver(id); }
  function onDrop(targetId: string) {
    if (!dragId || dragId === targetId) return;
    setSelected(prev => {
      const next = [...prev];
      const from = next.indexOf(dragId), to = next.indexOf(targetId);
      if (from === -1 || to === -1) return prev;
      next.splice(from, 1); next.splice(to, 0, dragId);
      persist(next);
      return next;
    });
    setDragId(null); setDragOver(null);
  }

  const reset = () => {
    setSelected([...HOME_WIDGET_DEFAULT]);
    persist([...HOME_WIDGET_DEFAULT]);
    notifications.show({ message: 'Dashboard reset to defaults', color: 'var(--color-primary-700)' });
  };

  // Resolved metadata for a selected pill
  function resolveTitle(id: string): { title: string; color: string } {
    const native = NATIVE_WIDGETS.find(w => w.id === id);
    if (native) return { title: native.title, color: native.color };
    const cop = WIDGETS.find(w => w.id === id);
    if (cop) return { title: cop.title, color: cop.color.startsWith('var(') ? '#4B3FFF' : cop.color };
    return { title: id, color: '#939598' };
  }

  return (
    <div style={{ maxWidth: 960 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title order={3} c="#323334">Dashboard Widgets</Title>
        <Text size="sm" c="#7B7D7F" mt={8}>
          Choose what appears in the dashboard grid on your home page. Toggle to add or remove,
          then drag the selected pills to set the order. Changes apply immediately.
        </Text>
      </div>

      {/* Selected pill list with drag-to-reorder */}
      {selected.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#939598', marginBottom: 8 }}>
            Currently showing · drag to reorder
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {selected.map((id, idx) => {
              const { title, color } = resolveTitle(id);
              const isDragging = dragId === id;
              const isOver = dragOver === id;
              return (
                <div
                  key={id}
                  draggable
                  onDragStart={() => onDragStart(id)}
                  onDragEnd={onDragEnd}
                  onDragOver={e => onDragOver(e, id)}
                  onDrop={() => onDrop(id)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '5px 10px 5px 8px',
                    background: isOver ? '#F7F8FD' : '#ffffff',
                    border: `1px solid ${isOver ? '#4B3FFF' : '#DDDEDE'}`,
                    borderLeft: `3px solid ${color}`,
                    borderRadius: 4, cursor: 'grab',
                    opacity: isDragging ? 0.4 : 1,
                    transition: 'all 120ms',
                    userSelect: 'none',
                  }}
                >
                  <IconGripVertical size={12} style={{ color: '#C8C9CA', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: '#939598', fontFamily: 'DM Mono, monospace', fontWeight: 600, flexShrink: 0 }}>
                    {idx + 1}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#323334', whiteSpace: 'nowrap' }}>
                    {title}
                  </span>
                  <button
                    onClick={() => toggle(id)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 16, height: 16, borderRadius: 4, border: 'none',
                      background: '#F2F2F2', cursor: 'pointer',
                      color: '#939598', flexShrink: 0, marginLeft: 2,
                      fontSize: 12, lineHeight: 1, fontWeight: 700,
                    }}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Native widgets ── */}
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#939598', marginBottom: 8 }}>
        Home page widgets
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, marginBottom: 24 }}>
        {NATIVE_WIDGETS.map(def => {
          const isOn = selected.includes(def.id);
          const Icon = NATIVE_ICONS[def.id];
          return (
            <PickerCard
              key={def.id}
              id={def.id}
              title={def.title}
              color={def.color}
              description={NATIVE_DESCRIPTIONS[def.id] || ''}
              icon={Icon}
              isOn={isOn}
              onToggle={toggle}
            />
          );
        })}
      </div>

      {/* ── COP widgets ── */}
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#939598', marginBottom: 8 }}>
        Live data widgets (from COP)
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
        {WIDGETS.map(def => {
          const isOn = selected.includes(def.id);
          const cardColor = def.color.startsWith('var(') ? '#4B3FFF' : def.color;
          return (
            <PickerCard
              key={def.id}
              id={def.id}
              title={def.title}
              color={cardColor}
              description={COP_DESCRIPTIONS[def.id] || 'Live module data'}
              icon={def.icon}
              isOn={isOn}
              onToggle={toggle}
            />
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={reset}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '7px 16px', border: '1px solid #DDDEDE', borderRadius: 4,
            background: '#ffffff', color: '#7B7D7F', fontSize: 12, fontWeight: 500,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <IconRefresh size={13} />
          Reset to defaults
        </button>
        <span style={{ fontSize: 12, color: '#ADAFB1' }}>
          {selected.length} widget{selected.length !== 1 ? 's' : ''} selected
        </span>
      </div>
    </div>
  );
}

// ── Picker card ───────────────────────────────────────────────

function PickerCard({ id, title, color, description, icon: Icon, isOn, onToggle }: {
  id: string; title: string; color: string; description: string;
  icon?: React.ComponentType<{ size?: number }>; isOn: boolean; onToggle: (id: string) => void;
}) {
  return (
    <button
      onClick={() => onToggle(id)}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8,
        padding: '16px 16px',
        background: isOn ? `${color}08` : '#ffffff',
        border: `1px solid ${isOn ? color : '#DDDEDE'}`,
        borderLeft: `4px solid ${isOn ? color : '#DDDEDE'}`,
        borderRadius: 4, cursor: 'pointer', textAlign: 'left',
        transition: 'all 140ms', position: 'relative', outline: 'none',
      }}
    >
      {/* Checkbox */}
      <div style={{
        position: 'absolute', top: 10, right: 10,
        width: 18, height: 18, borderRadius: 4,
        background: isOn ? color : 'transparent',
        border: `1.5px solid ${isOn ? color : '#DDDEDE'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 140ms',
      }}>
        {isOn && <IconCheck size={11} color="#fff" stroke={3} />}
      </div>

      {/* Icon + title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, paddingRight: 24 }}>
        {Icon && <div style={{ color, flexShrink: 0 }}><Icon size={16} /></div>}
        <span style={{ fontSize: 14, fontWeight: 600, color: isOn ? color : '#323334', lineHeight: 1.2 }}>
          {title}
        </span>
      </div>

      {/* Description */}
      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: isOn ? '#616365' : '#939598' }}>
        {description}
      </p>
    </button>
  );
}
